// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Router = (() => {
    let NodeTypes;
    (function (NodeTypes) {
        NodeTypes[NodeTypes["GROUP"] = 1] = "GROUP";
        NodeTypes[NodeTypes["CAT"] = 2] = "CAT";
        NodeTypes[NodeTypes["SYMBOL"] = 3] = "SYMBOL";
        NodeTypes[NodeTypes["OR"] = 4] = "OR";
        NodeTypes[NodeTypes["STAR"] = 5] = "STAR";
        NodeTypes[NodeTypes["LITERAL"] = 6] = "LITERAL";
        NodeTypes[NodeTypes["SLASH"] = 7] = "SLASH";
        NodeTypes[NodeTypes["DOT"] = 8] = "DOT";
    })(NodeTypes || (NodeTypes = {}));
    const isBrowser = typeof window !== "undefined";
    const UnescapedSpecials = "-._~!$&'()*+,;=:@"
        .split("")
        .map((s) => s.charCodeAt(0));
    const UnescapedRanges = [
        ["a", "z"],
        ["A", "Z"],
        ["0", "9"],
    ].map((range) => range.map((s) => s.charCodeAt(0)));
    class ParametersMissing extends Error {
        constructor(...keys) {
            super(`Route missing required keys: ${keys.join(", ")}`);
            this.keys = keys;
            Object.setPrototypeOf(this, Object.getPrototypeOf(this));
            this.name = ParametersMissing.name;
        }
    }
    const ReservedOptions = [
        "anchor",
        "trailing_slash",
        "subdomain",
        "host",
        "port",
        "protocol",
        "script_name",
    ];
    class Router {
        constructor(config = {}) {
            this.configuration = {
                prefix: "",
                default_url_options: {},
                special_options_key: "__options__",
                serializer: this.default_serializer.bind(this),
                deprecated_false_parameter_behavior: false,
                deprecated_nil_query_parameter_behavior: false,
                include_undefined_query_parameters: true,
            };
            this.configure(config);
        }
        configure(new_config) {
            var _a;
            if (new_config.prefix) {
                console.warn("JsRoutes configuration prefix option is deprecated in favor of default_url_options.script_name.");
            }
            (_a = new_config.serializer) !== null && _a !== void 0 ? _a : (new_config.serializer = this.default_serializer.bind(this));
            this.configuration = { ...this.configuration, ...new_config };
            return this.configuration;
        }
        config() {
            return { ...this.configuration };
        }
        serialize(object) {
            return this.configuration.serializer(object);
        }
        __route(parts_table, route_spec, absolute = false) {
            const required_params = [];
            const parts = [];
            const default_options = {};
            for (const [part, { r: required, d: value }] of Object.entries(parts_table)) {
                parts.push(part);
                if (required) {
                    required_params.push(part);
                }
                if (this.is_not_nullable(value)) {
                    default_options[part] = value;
                }
            }
            const result = (...args) => {
                return this.build_route(parts, required_params, default_options, route_spec, absolute, args);
            };
            result.requiredParams = () => required_params;
            result.toString = () => {
                return this.build_path_spec(route_spec);
            };
            return result;
        }
        default_serializer(value, prefix, array_element = false) {
            if (!prefix && !this.is_object(value)) {
                throw new Error("URL parameters should be a javascript hash");
            }
            prefix = prefix || "";
            const result = [];
            if (this.is_array(value)) {
                // Rails serializes an empty array nested inside another array as a nil
                // query value, while an empty object contributes no query parameter.
                if (value.length === 0) {
                    return array_element
                        ? this.default_serializer(null, prefix + "[]")
                        : "";
                }
                for (const element of value) {
                    const subvalue = this.default_serializer(element, prefix + "[]", true);
                    // Skip empty object results so arrays do not produce leading,
                    // trailing, or doubled "&" separators.
                    if (subvalue.length) {
                        result.push(subvalue);
                    }
                }
            }
            else if (this.is_object(value)) {
                for (let key in value) {
                    if (!this.hasProp(value, key))
                        continue;
                    let prop = value[key];
                    if (!this.configuration.include_undefined_query_parameters &&
                        prop === undefined) {
                        continue;
                    }
                    if (prefix) {
                        key = prefix + "[" + key + "]";
                    }
                    const subvalue = this.default_serializer(prop, key);
                    if (subvalue.length) {
                        result.push(subvalue);
                    }
                }
            }
            else {
                const key = encodeURIComponent(prefix);
                result.push(this.is_not_nullable(value) ||
                    this.configuration.deprecated_nil_query_parameter_behavior
                    ? key + "=" + encodeURIComponent("" + (value !== null && value !== void 0 ? value : ""))
                    : key);
            }
            return result.join("&");
        }
        extract_options(number_of_params, args) {
            const last_el = args[args.length - 1];
            if ((args.length > number_of_params && last_el === 0) ||
                (this.is_object(last_el) && !this.looks_like_serialized_model(last_el))) {
                if (this.is_object(last_el)) {
                    delete last_el[this.configuration.special_options_key];
                }
                return {
                    args: args.slice(0, args.length - 1),
                    options: last_el,
                };
            }
            else {
                return { args, options: {} };
            }
        }
        looks_like_serialized_model(object) {
            return (this.is_object(object) &&
                !(this.configuration.special_options_key in object) &&
                ("id" in object || "to_param" in object || "toParam" in object));
        }
        path_identifier(object) {
            const result = this.unwrap_path_identifier(object);
            return this.is_nullable(result) ||
                (this.configuration.deprecated_false_parameter_behavior &&
                    result === false)
                ? ""
                : "" + result;
        }
        unwrap_path_identifier(object) {
            let result = object;
            if (!this.is_object(object)) {
                return object;
            }
            if ("to_param" in object) {
                result = object.to_param;
            }
            else if ("toParam" in object) {
                result = object.toParam;
            }
            else if ("id" in object) {
                result = object.id;
            }
            else {
                result = object;
            }
            return this.is_callable(result) ? result.call(object) : result;
        }
        partition_parameters(parts, required_params, default_options, call_arguments) {
            let { args, options } = this.extract_options(parts.length, call_arguments);
            if (args.length > parts.length) {
                throw new Error("Too many parameters provided for path");
            }
            let use_all_parts = args.length > required_params.length;
            const parts_options = {
                ...this.configuration.default_url_options,
            };
            for (const key in options) {
                const value = options[key];
                if (!this.hasProp(options, key))
                    continue;
                use_all_parts = true;
                if (parts.includes(key)) {
                    parts_options[key] = value;
                }
            }
            options = {
                ...this.configuration.default_url_options,
                ...default_options,
                ...options,
            };
            const keyword_parameters = {};
            let query_parameters = {};
            for (const key in options) {
                if (!this.hasProp(options, key))
                    continue;
                const value = options[key];
                if (key === "params") {
                    if (this.is_object(value)) {
                        query_parameters = {
                            ...query_parameters,
                            ...value,
                        };
                    }
                    else {
                        throw new Error("params value should always be an object");
                    }
                }
                else if (this.is_reserved_option(key)) {
                    keyword_parameters[key] = value;
                }
                else {
                    if (!this.is_nullable(value) &&
                        (value !== default_options[key] || required_params.includes(key))) {
                        query_parameters[key] = value;
                    }
                }
            }
            const route_parts = use_all_parts ? parts : required_params;
            let i = 0;
            for (const part of route_parts) {
                if (i < args.length) {
                    const value = args[i];
                    if (!this.hasProp(parts_options, part)) {
                        query_parameters[part] = value;
                        ++i;
                    }
                }
            }
            return { keyword_parameters, query_parameters };
        }
        build_route(parts, required_params, default_options, route, absolute, args) {
            const { keyword_parameters, query_parameters } = this.partition_parameters(parts, required_params, default_options, args);
            let { trailing_slash, anchor, script_name } = keyword_parameters;
            const missing_params = required_params.filter((param) => !this.hasProp(query_parameters, param) ||
                this.is_nullable(query_parameters[param]));
            if (missing_params.length) {
                throw new ParametersMissing(...missing_params);
            }
            let result = this.get_prefix() + this.visit(route, query_parameters);
            if (trailing_slash) {
                result = result.replace(/(.*?)[/]?$/, "$1/");
            }
            const url_params = this.serialize(query_parameters);
            if (url_params.length) {
                result += "?" + url_params;
            }
            if (anchor) {
                result += "#" + anchor;
            }
            if (script_name) {
                const last_index = script_name.length - 1;
                if (script_name[last_index] == "/" && result[0] == "/") {
                    script_name = script_name.slice(0, last_index);
                }
                result = script_name + result;
            }
            if (absolute) {
                result = this.route_url(keyword_parameters) + result;
            }
            return result;
        }
        visit(route, parameters, optional = false) {
            switch (route[0]) {
                case NodeTypes.GROUP:
                    return this.visit(route[1], parameters, true);
                case NodeTypes.CAT:
                    return this.visit_cat(route, parameters, optional);
                case NodeTypes.SYMBOL:
                    return this.visit_symbol(route, parameters, optional);
                case NodeTypes.STAR:
                    return this.visit_globbing(route[1], parameters, true);
                case NodeTypes.LITERAL:
                case NodeTypes.SLASH:
                case NodeTypes.DOT:
                    return route[1];
                default:
                    throw new Error("Unknown Rails node type");
            }
        }
        is_not_nullable(object) {
            return !this.is_nullable(object);
        }
        is_nullable(object) {
            return object === undefined || object === null;
        }
        visit_cat(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [_type, left, right], parameters, optional) {
            const left_part = this.visit(left, parameters, optional);
            let right_part = this.visit(right, parameters, optional);
            if (optional &&
                ((this.is_optional_node(left[0]) && !left_part) ||
                    (this.is_optional_node(right[0]) && !right_part))) {
                return "";
            }
            // if left_part ends on '/' and right_part starts on '/'
            if (left_part[left_part.length - 1] === "/" && right_part[0] === "/") {
                // strip slash from right_part
                // to prevent double slash
                right_part = right_part.substring(1);
            }
            return left_part + right_part;
        }
        visit_symbol(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [_type, key], parameters, optional) {
            const value = this.path_identifier(parameters[key]);
            delete parameters[key];
            if (value.length) {
                return this.encode_segment(value);
            }
            if (optional) {
                return "";
            }
            else {
                throw new ParametersMissing(key);
            }
        }
        encode_segment(segment) {
            if (segment.match(/^[a-zA-Z0-9-]$/)) {
                // Performance optimization for 99% of cases
                return segment;
            }
            return (segment.match(/./gu) || [])
                .map((ch) => {
                const code = ch.charCodeAt(0);
                if (UnescapedRanges.find((range) => code >= range[0] && code <= range[1]) ||
                    UnescapedSpecials.includes(code)) {
                    return ch;
                }
                else {
                    return encodeURIComponent(ch);
                }
            })
                .join("");
        }
        is_optional_node(node) {
            return [NodeTypes.STAR, NodeTypes.SYMBOL, NodeTypes.CAT].includes(node);
        }
        build_path_spec(route, wildcard = false) {
            let key;
            switch (route[0]) {
                case NodeTypes.GROUP:
                    return `(${this.build_path_spec(route[1])})`;
                case NodeTypes.CAT:
                    return (this.build_path_spec(route[1]) + this.build_path_spec(route[2]));
                case NodeTypes.STAR:
                    return this.build_path_spec(route[1], true);
                case NodeTypes.SYMBOL:
                    key = route[1];
                    if (wildcard) {
                        return (key.startsWith("*") ? "" : "*") + key;
                    }
                    else {
                        return ":" + key;
                    }
                case NodeTypes.SLASH:
                case NodeTypes.DOT:
                case NodeTypes.LITERAL:
                    return route[1];
                default:
                    throw new Error("Unknown Rails node type");
            }
        }
        visit_globbing(route, parameters, optional) {
            const key = route[1];
            let value = parameters[key];
            delete parameters[key];
            if (this.is_nullable(value)) {
                return this.visit(route, parameters, optional);
            }
            if (this.is_array(value)) {
                value = value.join("/");
            }
            const result = this.path_identifier(value);
            return encodeURI(result);
        }
        get_prefix() {
            const prefix = this.configuration.prefix;
            return prefix.match("/$")
                ? prefix.substring(0, prefix.length - 1)
                : prefix;
        }
        route_url(route_defaults) {
            const hostname = route_defaults.host || this.current_host();
            if (!hostname) {
                return "";
            }
            const subdomain = route_defaults.subdomain
                ? route_defaults.subdomain + "."
                : "";
            const protocol = route_defaults.protocol || this.current_protocol();
            let port = route_defaults.port ||
                (!route_defaults.host ? this.current_port() : undefined);
            port = port ? ":" + port : "";
            return protocol + "://" + subdomain + hostname + port;
        }
        current_host() {
            var _a;
            return (isBrowser && ((_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.hostname)) || "";
        }
        current_protocol() {
            var _a, _b;
            return ((isBrowser && ((_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.protocol) === null || _b === void 0 ? void 0 : _b.replace(/:$/, ""))) || "http");
        }
        current_port() {
            var _a;
            return (isBrowser && ((_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.port)) || "";
        }
        is_object(value) {
            return (typeof value === "object" &&
                Object.prototype.toString.call(value) === "[object Object]");
        }
        is_array(object) {
            return object instanceof Array;
        }
        is_callable(object) {
            return typeof object === "function" && !!object.call;
        }
        is_reserved_option(key) {
            return ReservedOptions.includes(key);
        }
        hasProp(value, key) {
            return Object.prototype.hasOwnProperty.call(value, key);
        }
    }
    return Router;
})();
const __jsr = (() => {
    var _a;
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    ;
    const Modules = {
        references: {
            CJS: {
                define(routes) {
                    if (module) {
                        // Some javascript processors (like vite/rolldown)
                        // warn on using module dot exports in an ESM module.
                        // This just obfuscates that assignment a little so
                        // users don't get a warning they can't fix.
                        const _mod = module;
                        _mod.exports = routes;
                    }
                },
                isSupported() {
                    return typeof module === "object";
                },
            },
            AMD: {
                define(routes) {
                    if (define) {
                        define([], function () {
                            return routes;
                        });
                    }
                },
                isSupported() {
                    return typeof define === "function" && !!define.amd;
                },
            },
            UMD: {
                define(routes) {
                    if (Modules.references.AMD.isSupported()) {
                        Modules.references.AMD.define(routes);
                    }
                    else {
                        if (Modules.references.CJS.isSupported()) {
                            try {
                                Modules.references.CJS.define(routes);
                            }
                            catch (error) {
                                if (error.name !== "TypeError")
                                    throw error;
                            }
                        }
                    }
                },
                isSupported() {
                    return (Modules.references.AMD.isSupported() ||
                        Modules.references.CJS.isSupported());
                },
            },
            ESM: {
                define() {
                    // Module can only be defined using ruby code generation
                },
                isSupported() {
                    // Its impossible to check if "export" keyword is supported
                    return true;
                },
            },
            NIL: {
                define() {
                    // Defined using RubyVariables . WRAPPER
                },
                isSupported() {
                    return true;
                },
            },
            DTS: {
                // Acts the same as ESM
                define(routes) {
                    Modules.references.ESM.define(routes);
                },
                isSupported() {
                    return Modules.references.ESM.isSupported();
                },
            },
            PKG: {
                // Acts the same as ESM
                define() {
                    Modules.references.ESM.define(new Router());
                },
                isSupported() {
                    return Modules.references.ESM.isSupported();
                },
            },
        },
        is_module_supported(name) {
            return this.references[name].isSupported();
        },
        ensure_module_supported(name) {
            if (!this.is_module_supported(name)) {
                throw new Error(`${name} is not supported by runtime`);
            }
        },
        define_module(name, module) {
            this.ensure_module_supported(name);
            this.references[name].define(module);
            return module;
        },
    };
    const router = new Router({
        prefix: "",
        default_url_options: {},
        special_options_key: "_options",
        serializer: (_a = null) !== null && _a !== void 0 ? _a : undefined,
        deprecated_false_parameter_behavior: false,
        deprecated_nil_query_parameter_behavior: true,
        include_undefined_query_parameters: false,
    });
    const __route = router.__route.bind(router);
    return Modules.define_module("ESM", {
        __route,
        configure: router.configure.bind(router),
        config: router.config.bind(router),
        serialize: router.serialize.bind(router),
        ...{},
    });
})();
export const configure = __jsr.configure;

export const config = __jsr.config;

export const serialize = __jsr.serialize;

export const __route = __jsr.__route;

/**
 * Generates rails route to
 * /activate(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const activate_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"activate"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /activate(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const activate_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"activate"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /activations/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const activation_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"activations"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /activations/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const activation_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"activations"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /activations(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const activations_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"activations"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /activations(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const activations_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"activations"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /api/employee_user_archivals(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_employee_user_archivals_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"employee_user_archivals"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /api/employee_user_archivals(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_employee_user_archivals_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"employee_user_archivals"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /api/google/drive_token(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_google_drive_token_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"google"],[2,[7,"/"],[2,[6,"drive_token"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/google/drive_token(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_google_drive_token_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"google"],[2,[7,"/"],[2,[6,"drive_token"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /api/owls/bulk/classes(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_bulk_classes_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"bulk"],[2,[7,"/"],[2,[6,"classes"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/owls/bulk/classes(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_bulk_classes_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"bulk"],[2,[7,"/"],[2,[6,"classes"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /api/owls/bulk/customers(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_bulk_customers_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"bulk"],[2,[7,"/"],[2,[6,"customers"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/owls/bulk/customers(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_bulk_customers_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"bulk"],[2,[7,"/"],[2,[6,"customers"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /api/owls/bulk/users(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_bulk_users_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"bulk"],[2,[7,"/"],[2,[6,"users"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/owls/bulk/users(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_bulk_users_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"bulk"],[2,[7,"/"],[2,[6,"users"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /api/owls/customer/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_customer_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"customer"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/owls/customer/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_customer_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"customer"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /api/owls/customer(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_customer_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"customer"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/owls/customer(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_customer_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"customer"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /api/owls/class/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_klass_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"class"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/owls/class/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_klass_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"class"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /api/owls/class(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_klasses_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"class"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/owls/class(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_klasses_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"class"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /api/owls/user/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_user_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"user"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/owls/user/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_user_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"user"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /api/owls/user(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_user_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"user"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/owls/user(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_owls_user_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"user"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /api/v1/curriculum_navigate(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_curriculum_navigate_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"curriculum_navigate"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/v1/curriculum_navigate(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_curriculum_navigate_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"curriculum_navigate"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /api/v1/customers/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_customer_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"customers"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/v1/customers/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_customer_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"customers"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /api/v1/customers(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_customers_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"customers"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/v1/customers(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_customers_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"customers"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /api/v1/grades_for_collection(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_grades_for_collection_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"grades_for_collection"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/v1/grades_for_collection(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_grades_for_collection_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"grades_for_collection"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /api/v1/lessons_for_section(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_lessons_for_section_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"lessons_for_section"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/v1/lessons_for_section(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_lessons_for_section_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"lessons_for_section"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /api/v1/sections_for_unit(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_sections_for_unit_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"sections_for_unit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/v1/sections_for_unit(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_sections_for_unit_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"sections_for_unit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /api/v1/units_for_grade(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_units_for_grade_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"units_for_grade"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/v1/units_for_grade(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const api_v1_units_for_grade_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"units_for_grade"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /api/owls/user/:id/archive(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const archive_api_owls_user_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"user"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"archive"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/owls/user/:id/archive(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const archive_api_owls_user_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"owls"],[2,[7,"/"],[2,[6,"user"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"archive"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /assignments/archive(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const assignments_archive_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[6,"archive"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /assignments/archive(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const assignments_archive_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[6,"archive"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /assignments/inactive(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const assignments_inactive_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[6,"inactive"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /assignments/inactive(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const assignments_inactive_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[6,"inactive"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /audios/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const audio_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"audios"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /audios/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const audio_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"audios"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /audios(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const audios_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"audios"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /audios(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const audios_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"audios"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /auth/:provider/callback(.:format)
 * @param {any} provider
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const auth_callback_url = /*#__PURE__*/ __route({"provider":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[3,"provider"],[2,[7,"/"],[2,[6,"callback"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /auth/:provider/callback(.:format)
 * @param {any} provider
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const auth_callback_path = /*#__PURE__*/ __route({"provider":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[3,"provider"],[2,[7,"/"],[2,[6,"callback"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /auth/failure(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const auth_failure_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[6,"failure"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /auth/failure(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const auth_failure_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[6,"failure"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /auth/imlp/callback(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const auth_imlp_callback_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[6,"imlp"],[2,[7,"/"],[2,[6,"callback"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /auth/imlp/callback(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const auth_imlp_callback_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[6,"imlp"],[2,[7,"/"],[2,[6,"callback"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /auth/imlp/login(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const auth_imlp_login_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[6,"imlp"],[2,[7,"/"],[2,[6,"login"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /auth/imlp/login(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const auth_imlp_login_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[6,"imlp"],[2,[7,"/"],[2,[6,"login"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /auth/imlp(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const auth_imlp_root_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[6,"imlp"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /auth/imlp(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const auth_imlp_root_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[6,"imlp"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /auth/imlp/token(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const auth_imlp_token_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[6,"imlp"],[2,[7,"/"],[2,[6,"token"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /auth/imlp/token(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const auth_imlp_token_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[6,"imlp"],[2,[7,"/"],[2,[6,"token"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /authored_lesson_plans(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const authored_lesson_plans_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"authored_lesson_plans"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /authored_lesson_plans(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const authored_lesson_plans_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"authored_lesson_plans"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /barred_words(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const barred_words_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"barred_words"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /barred_words(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const barred_words_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"barred_words"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /saved/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const bookmark_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"saved"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /saved/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const bookmark_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"saved"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /bookmark_folders/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const bookmark_folder_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"bookmark_folders"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /bookmark_folders/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const bookmark_folder_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"bookmark_folders"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /saved/:bookmark_id/folder_placements/:id(.:format)
 * @param {any} bookmark_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const bookmark_folder_placement_url = /*#__PURE__*/ __route({"bookmark_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"saved"],[2,[7,"/"],[2,[3,"bookmark_id"],[2,[7,"/"],[2,[6,"folder_placements"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /saved/:bookmark_id/folder_placements/:id(.:format)
 * @param {any} bookmark_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const bookmark_folder_placement_path = /*#__PURE__*/ __route({"bookmark_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"saved"],[2,[7,"/"],[2,[3,"bookmark_id"],[2,[7,"/"],[2,[6,"folder_placements"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /saved/:bookmark_id/folder_placements(.:format)
 * @param {any} bookmark_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const bookmark_folder_placements_url = /*#__PURE__*/ __route({"bookmark_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"saved"],[2,[7,"/"],[2,[3,"bookmark_id"],[2,[7,"/"],[2,[6,"folder_placements"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /saved/:bookmark_id/folder_placements(.:format)
 * @param {any} bookmark_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const bookmark_folder_placements_path = /*#__PURE__*/ __route({"bookmark_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"saved"],[2,[7,"/"],[2,[3,"bookmark_id"],[2,[7,"/"],[2,[6,"folder_placements"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /bookmark_folders(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const bookmark_folders_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"bookmark_folders"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /bookmark_folders(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const bookmark_folders_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"bookmark_folders"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /saved(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const bookmarks_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"saved"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /saved(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const bookmarks_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"saved"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /browse(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const browse_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"browse"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /browse(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const browse_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"browse"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /cards/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const card_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"cards"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /cards/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const card_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"cards"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /cards/:card_id/teaching_notes(.:format)
 * @param {any} card_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const card_teaching_notes_url = /*#__PURE__*/ __route({"card_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"cards"],[2,[7,"/"],[2,[3,"card_id"],[2,[7,"/"],[2,[6,"teaching_notes"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /cards/:card_id/teaching_notes(.:format)
 * @param {any} card_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const card_teaching_notes_path = /*#__PURE__*/ __route({"card_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"cards"],[2,[7,"/"],[2,[3,"card_id"],[2,[7,"/"],[2,[6,"teaching_notes"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /changelog(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const changelog_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"changelog"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /changelog(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const changelog_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"changelog"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /api/v1/teachers/:id/classrooms(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const classrooms_api_v1_teacher_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"teachers"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"classrooms"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /api/v1/teachers/:id/classrooms(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const classrooms_api_v1_teacher_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"api"],[2,[7,"/"],[2,[6,"v1"],[2,[7,"/"],[2,[6,"teachers"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"classrooms"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /clever_auth_check(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const clever_auth_check_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"clever_auth_check"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /clever_auth_check(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const clever_auth_check_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"clever_auth_check"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /auth/clever(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const clever_login_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[6,"clever"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /auth/clever(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const clever_login_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"auth"],[2,[7,"/"],[2,[6,"clever"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/collections/:id/collection_metadata(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const collection_metadata_site_admin_collection_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"collection_metadata"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/collections/:id/collection_metadata(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const collection_metadata_site_admin_collection_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"collection_metadata"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /comments/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const comment_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"comments"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /comments/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const comment_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"comments"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /comments/:comment_id/comments(.:format)
 * @param {any} comment_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const comment_comments_url = /*#__PURE__*/ __route({"comment_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"comments"],[2,[7,"/"],[2,[3,"comment_id"],[2,[7,"/"],[2,[6,"comments"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /comments/:comment_id/comments(.:format)
 * @param {any} comment_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const comment_comments_path = /*#__PURE__*/ __route({"comment_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"comments"],[2,[7,"/"],[2,[3,"comment_id"],[2,[7,"/"],[2,[6,"comments"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /contact_form/submit(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const contact_form_submit_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"contact_form"],[2,[7,"/"],[2,[6,"submit"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /contact_form/submit(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const contact_form_submit_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"contact_form"],[2,[7,"/"],[2,[6,"submit"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /lti/content_items/:content_type/:id(.:format)
 * @param {any} content_type
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const content_item_lti_content_items_url = /*#__PURE__*/ __route({"content_type":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lti"],[2,[7,"/"],[2,[6,"content_items"],[2,[7,"/"],[2,[3,"content_type"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /lti/content_items/:content_type/:id(.:format)
 * @param {any} content_type
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const content_item_lti_content_items_path = /*#__PURE__*/ __route({"content_type":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lti"],[2,[7,"/"],[2,[6,"content_items"],[2,[7,"/"],[2,[3,"content_type"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /lti_v13/content_items/:content_type/:id(.:format)
 * @param {any} content_type
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const content_item_lti_v13_content_items_url = /*#__PURE__*/ __route({"content_type":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"content_items"],[2,[7,"/"],[2,[3,"content_type"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /lti_v13/content_items/:content_type/:id(.:format)
 * @param {any} content_type
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const content_item_lti_v13_content_items_path = /*#__PURE__*/ __route({"content_type":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"content_items"],[2,[7,"/"],[2,[3,"content_type"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/enrollments/create_bulk(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const create_bulk_teacher_klass_enrollments_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"enrollments"],[2,[7,"/"],[2,[6,"create_bulk"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/enrollments/create_bulk(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const create_bulk_teacher_klass_enrollments_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"enrollments"],[2,[7,"/"],[2,[6,"create_bulk"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/ai_access/csv_import(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const csv_import_site_admin_ai_access_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"ai_access"],[2,[7,"/"],[2,[6,"csv_import"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/ai_access/csv_import(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const csv_import_site_admin_ai_access_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"ai_access"],[2,[7,"/"],[2,[6,"csv_import"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/ai_access/csv_preview(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const csv_preview_site_admin_ai_access_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"ai_access"],[2,[7,"/"],[2,[6,"csv_preview"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/ai_access/csv_preview(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const csv_preview_site_admin_ai_access_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"ai_access"],[2,[7,"/"],[2,[6,"csv_preview"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /customer_maintenance(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const customer_maintenance_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"customer_maintenance"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /customer_maintenance(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const customer_maintenance_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"customer_maintenance"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /site_select(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const customer_selects_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_select"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /site_select(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const customer_selects_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_select"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /session/from_public(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const customer_session_from_public_session_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"session"],[2,[7,"/"],[2,[6,"from_public"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /session/from_public(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const customer_session_from_public_session_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"session"],[2,[7,"/"],[2,[6,"from_public"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /sites(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const customers_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"sites"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /sites(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const customers_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"sites"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /data_dashboard/download/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_download_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"download"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /data_dashboard/download/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_download_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"download"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /data_dashboard/download(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_download_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"download"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /data_dashboard/download(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_download_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"download"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /data_dashboard(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /data_dashboard(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /data_dashboard/performance/available_customers(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_available_customers_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"available_customers"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /data_dashboard/performance/available_customers(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_available_customers_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"available_customers"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /data_dashboard/performance/category_items_rollup(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_category_items_rollup_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"category_items_rollup"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /data_dashboard/performance/category_items_rollup(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_category_items_rollup_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"category_items_rollup"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /data_dashboard/performance/category_rollup(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_category_rollup_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"category_rollup"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /data_dashboard/performance/category_rollup(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_category_rollup_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"category_rollup"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /data_dashboard/performance/category_standards_rollup(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_category_standards_rollup_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"category_standards_rollup"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /data_dashboard/performance/category_standards_rollup(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_category_standards_rollup_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"category_standards_rollup"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /data_dashboard/performance/ranked_standards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_ranked_standards_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"ranked_standards"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /data_dashboard/performance/ranked_standards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_ranked_standards_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"ranked_standards"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /data_dashboard/performance/score_bands(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_score_bands_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"score_bands"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /data_dashboard/performance/score_bands(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_score_bands_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"score_bands"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /data_dashboard/performance/standards_alignment(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_standards_alignment_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"standards_alignment"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /data_dashboard/performance/standards_alignment(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_performance_standards_alignment_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"performance"],[2,[7,"/"],[2,[6,"standards_alignment"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /data_dashboard/usage(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_usage_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"usage"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /data_dashboard/usage(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const data_dashboard_usage_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"data_dashboard"],[2,[7,"/"],[2,[6,"usage"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /documents/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const document_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"documents"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /documents/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const document_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"documents"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /documents(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const documents_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"documents"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /documents(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const documents_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"documents"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /pilot_guide/companion_guide/download(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const download_pilot_guide_companion_guide_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"pilot_guide"],[2,[7,"/"],[2,[6,"companion_guide"],[2,[7,"/"],[2,[6,"download"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /pilot_guide/companion_guide/download(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const download_pilot_guide_companion_guide_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"pilot_guide"],[2,[7,"/"],[2,[6,"companion_guide"],[2,[7,"/"],[2,[6,"download"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /pilot_guide/observation_tool/download(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const download_pilot_guide_observation_tool_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"pilot_guide"],[2,[7,"/"],[2,[6,"observation_tool"],[2,[7,"/"],[2,[6,"download"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /pilot_guide/observation_tool/download(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const download_pilot_guide_observation_tool_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"pilot_guide"],[2,[7,"/"],[2,[6,"observation_tool"],[2,[7,"/"],[2,[6,"download"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /activations/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_activation_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"activations"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /activations/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_activation_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"activations"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /audios/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_audio_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"audios"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /audios/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_audio_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"audios"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /documents/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_document_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"documents"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /documents/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_document_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"documents"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /geogebra_items/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_geogebra_item_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"geogebra_items"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /geogebra_items/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_geogebra_item_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"geogebra_items"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /htmls/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_html_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"htmls"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /htmls/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_html_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"htmls"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /hyperlinks/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_hyperlink_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"hyperlinks"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /hyperlinks/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_hyperlink_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"hyperlinks"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /images/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_image_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"images"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /images/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_image_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"images"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_lesson_plan_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_lesson_plan_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/revision/edit(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_lesson_plan_revision_url = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"revision"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/revision/edit(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_lesson_plan_revision_path = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"revision"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /password_resets/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_password_reset_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"password_resets"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /password_resets/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_password_reset_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"password_resets"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /resources/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_resource_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /resources/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_resource_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /resources/:resource_id/revision/edit(.:format)
 * @param {any} resource_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_resource_revision_url = /*#__PURE__*/ __route({"resource_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"resource_id"],[2,[7,"/"],[2,[6,"revision"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /resources/:resource_id/revision/edit(.:format)
 * @param {any} resource_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_resource_revision_path = /*#__PURE__*/ __route({"resource_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"resource_id"],[2,[7,"/"],[2,[6,"revision"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/standard_initiatives/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_site_admin_standard_initiative_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_initiatives"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/standard_initiatives/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_site_admin_standard_initiative_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_initiatives"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /slides/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_slide_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"slides"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /slides/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_slide_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"slides"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /spreadsheets/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_spreadsheet_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"spreadsheets"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /spreadsheets/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_spreadsheet_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"spreadsheets"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /student/session/edit(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_student_session_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"session"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /student/session/edit(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_student_session_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"session"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /user/edit(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_user_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"user"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /user/edit(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_user_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"user"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /videos/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_video_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"videos"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /videos/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_video_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"videos"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /wikis/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_wiki_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"wikis"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /wikis/:id/edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const edit_wiki_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"wikis"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /embed(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const embed_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"embed"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /embed(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const embed_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"embed"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:id/feedback-settings(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const feedback_settings_teacher_klass_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"feedback-settings"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:id/feedback-settings(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const feedback_settings_teacher_klass_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"feedback-settings"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /file_store/files/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const file_store_file_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"file_store"],[2,[7,"/"],[2,[6,"files"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /file_store/files/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const file_store_file_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"file_store"],[2,[7,"/"],[2,[6,"files"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /file_store/sign_url(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const file_store_sign_url_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"file_store"],[2,[7,"/"],[2,[6,"sign_url"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /file_store/sign_url(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const file_store_sign_url_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"file_store"],[2,[7,"/"],[2,[6,"sign_url"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /file_store/upload_token(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const file_store_upload_token_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"file_store"],[2,[7,"/"],[2,[6,"upload_token"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /file_store/upload_token(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const file_store_upload_token_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"file_store"],[2,[7,"/"],[2,[6,"upload_token"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /filestack_options(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const filestack_options_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"filestack_options"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /filestack_options(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const filestack_options_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"filestack_options"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/assignments/:id/gallery/cuethink-gallery(.:format)
 * @param {any} klass_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const gallery_cuethink_gallery_teacher_klass_lz_code_view_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"gallery"],[2,[7,"/"],[2,[6,"cuethink-gallery"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/assignments/:id/gallery/cuethink-gallery(.:format)
 * @param {any} klass_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const gallery_cuethink_gallery_teacher_klass_lz_code_view_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"gallery"],[2,[7,"/"],[2,[6,"cuethink-gallery"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/assignments/:id/gallery/feedback-settings(.:format)
 * @param {any} klass_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const gallery_feedback_settings_teacher_klass_lz_code_view_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"gallery"],[2,[7,"/"],[2,[6,"feedback-settings"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/assignments/:id/gallery/feedback-settings(.:format)
 * @param {any} klass_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const gallery_feedback_settings_teacher_klass_lz_code_view_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"gallery"],[2,[7,"/"],[2,[6,"feedback-settings"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/assignments/:id/gallery/review-feedback(.:format)
 * @param {any} klass_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const gallery_review_feedback_teacher_klass_lz_code_view_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"gallery"],[2,[7,"/"],[2,[6,"review-feedback"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/assignments/:id/gallery/review-feedback(.:format)
 * @param {any} klass_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const gallery_review_feedback_teacher_klass_lz_code_view_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"gallery"],[2,[7,"/"],[2,[6,"review-feedback"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/assignments/:id/gallery(.:format)
 * @param {any} klass_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const gallery_teacher_klass_lz_code_view_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"gallery"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/assignments/:id/gallery(.:format)
 * @param {any} klass_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const gallery_teacher_klass_lz_code_view_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"gallery"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /geogebra_items/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const geogebra_item_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"geogebra_items"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /geogebra_items/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const geogebra_item_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"geogebra_items"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /geogebra_items(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const geogebra_items_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"geogebra_items"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /geogebra_items(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const geogebra_items_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"geogebra_items"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /get-file(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const get_file_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"get-file"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /get-file(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const get_file_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"get-file"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /google_classroom/courses/:course_id/students(.:format)
 * @param {any} course_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const google_classroom_course_students_url = /*#__PURE__*/ __route({"course_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"courses"],[2,[7,"/"],[2,[3,"course_id"],[2,[7,"/"],[2,[6,"students"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /google_classroom/courses/:course_id/students(.:format)
 * @param {any} course_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const google_classroom_course_students_path = /*#__PURE__*/ __route({"course_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"courses"],[2,[7,"/"],[2,[3,"course_id"],[2,[7,"/"],[2,[6,"students"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /google_classroom/courses(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const google_classroom_courses_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"courses"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /google_classroom/courses(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const google_classroom_courses_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"courses"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /google_classroom/data_sync_requests(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const google_classroom_data_sync_requests_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"data_sync_requests"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /google_classroom/data_sync_requests(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const google_classroom_data_sync_requests_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"data_sync_requests"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /google_classroom/profile(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const google_classroom_profile_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"profile"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /google_classroom/profile(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const google_classroom_profile_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"profile"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /item_analysis/suggestions/grade_levels(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grade_levels_item_analysis_suggestions_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"suggestions"],[2,[7,"/"],[2,[6,"grade_levels"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /item_analysis/suggestions/grade_levels(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grade_levels_item_analysis_suggestions_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"suggestions"],[2,[7,"/"],[2,[6,"grade_levels"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /grading/:lz_code(.:format)
 * @param {any} lz_code
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grading_url = /*#__PURE__*/ __route({"lz_code":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"grading"],[2,[7,"/"],[2,[3,"lz_code"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /grading/:lz_code(.:format)
 * @param {any} lz_code
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grading_path = /*#__PURE__*/ __route({"lz_code":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"grading"],[2,[7,"/"],[2,[3,"lz_code"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /grading/:lz_code/sessions/:lz_code_view_id(.:format)
 * @param {any} lz_code
 * @param {any} lz_code_view_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grading_session_url = /*#__PURE__*/ __route({"lz_code":{"r":true},"lz_code_view_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"grading"],[2,[7,"/"],[2,[3,"lz_code"],[2,[7,"/"],[2,[6,"sessions"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /grading/:lz_code/sessions/:lz_code_view_id(.:format)
 * @param {any} lz_code
 * @param {any} lz_code_view_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grading_session_path = /*#__PURE__*/ __route({"lz_code":{"r":true},"lz_code_view_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"grading"],[2,[7,"/"],[2,[3,"lz_code"],[2,[7,"/"],[2,[6,"sessions"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /grading/:lz_code/sessions/:lz_code_view_id/activate(.:format)
 * @param {any} lz_code
 * @param {any} lz_code_view_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grading_session_activate_url = /*#__PURE__*/ __route({"lz_code":{"r":true},"lz_code_view_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"grading"],[2,[7,"/"],[2,[3,"lz_code"],[2,[7,"/"],[2,[6,"sessions"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"activate"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /grading/:lz_code/sessions/:lz_code_view_id/activate(.:format)
 * @param {any} lz_code
 * @param {any} lz_code_view_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grading_session_activate_path = /*#__PURE__*/ __route({"lz_code":{"r":true},"lz_code_view_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"grading"],[2,[7,"/"],[2,[3,"lz_code"],[2,[7,"/"],[2,[6,"sessions"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"activate"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /grading/:lz_code/sessions/:lz_code_view_id/comment(.:format)
 * @param {any} lz_code
 * @param {any} lz_code_view_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grading_session_comment_url = /*#__PURE__*/ __route({"lz_code":{"r":true},"lz_code_view_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"grading"],[2,[7,"/"],[2,[3,"lz_code"],[2,[7,"/"],[2,[6,"sessions"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"comment"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /grading/:lz_code/sessions/:lz_code_view_id/comment(.:format)
 * @param {any} lz_code
 * @param {any} lz_code_view_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grading_session_comment_path = /*#__PURE__*/ __route({"lz_code":{"r":true},"lz_code_view_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"grading"],[2,[7,"/"],[2,[3,"lz_code"],[2,[7,"/"],[2,[6,"sessions"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"comment"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /grading/:lz_code/sessions/:lz_code_view_id/responses/:learnosity_response_id/comment(.:format)
 * @param {any} lz_code
 * @param {any} lz_code_view_id
 * @param {any} learnosity_response_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grading_session_response_comment_url = /*#__PURE__*/ __route({"lz_code":{"r":true},"lz_code_view_id":{"r":true},"learnosity_response_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"grading"],[2,[7,"/"],[2,[3,"lz_code"],[2,[7,"/"],[2,[6,"sessions"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"responses"],[2,[7,"/"],[2,[3,"learnosity_response_id"],[2,[7,"/"],[2,[6,"comment"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /grading/:lz_code/sessions/:lz_code_view_id/responses/:learnosity_response_id/comment(.:format)
 * @param {any} lz_code
 * @param {any} lz_code_view_id
 * @param {any} learnosity_response_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grading_session_response_comment_path = /*#__PURE__*/ __route({"lz_code":{"r":true},"lz_code_view_id":{"r":true},"learnosity_response_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"grading"],[2,[7,"/"],[2,[3,"lz_code"],[2,[7,"/"],[2,[6,"sessions"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"responses"],[2,[7,"/"],[2,[3,"learnosity_response_id"],[2,[7,"/"],[2,[6,"comment"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /grading/:lz_code/sessions/:lz_code_view_id/responses(.:format)
 * @param {any} lz_code
 * @param {any} lz_code_view_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grading_session_responses_url = /*#__PURE__*/ __route({"lz_code":{"r":true},"lz_code_view_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"grading"],[2,[7,"/"],[2,[3,"lz_code"],[2,[7,"/"],[2,[6,"sessions"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"responses"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /grading/:lz_code/sessions/:lz_code_view_id/responses(.:format)
 * @param {any} lz_code
 * @param {any} lz_code_view_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const grading_session_responses_path = /*#__PURE__*/ __route({"lz_code":{"r":true},"lz_code_view_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"grading"],[2,[7,"/"],[2,[3,"lz_code"],[2,[7,"/"],[2,[6,"sessions"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"responses"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:id/handout(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const handout_lesson_plan_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"handout"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:id/handout(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const handout_lesson_plan_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"handout"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /heartbeat(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const heartbeat_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"heartbeat"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /heartbeat(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const heartbeat_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"heartbeat"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /home(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const home_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"home"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /home(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const home_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"home"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /htmls/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const html_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"htmls"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /htmls/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const html_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"htmls"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /htmls(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const htmls_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"htmls"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /htmls(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const htmls_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"htmls"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /hyperlinks/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const hyperlink_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"hyperlinks"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /hyperlinks/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const hyperlink_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"hyperlinks"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /hyperlinks(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const hyperlinks_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"hyperlinks"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /hyperlinks(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const hyperlinks_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"hyperlinks"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /images/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const image_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"images"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /images/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const image_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"images"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /images(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const images_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"images"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /images(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const images_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"images"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/enrollments/index_bulk(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const index_bulk_teacher_klass_enrollments_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"enrollments"],[2,[7,"/"],[2,[6,"index_bulk"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/enrollments/index_bulk(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const index_bulk_teacher_klass_enrollments_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"enrollments"],[2,[7,"/"],[2,[6,"index_bulk"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/ai_access/individual_users(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const individual_users_site_admin_ai_access_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"ai_access"],[2,[7,"/"],[2,[6,"individual_users"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/ai_access/individual_users(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const individual_users_site_admin_ai_access_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"ai_access"],[2,[7,"/"],[2,[6,"individual_users"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:id/inline_edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const inline_edit_lesson_plan_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"inline_edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:id/inline_edit(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const inline_edit_lesson_plan_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"inline_edit"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /item_analysis/average_score(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_average_score_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"average_score"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /item_analysis/average_score(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_average_score_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"average_score"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /item_analysis(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /item_analysis(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /item_analysis/item_previews(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_item_previews_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"item_previews"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /item_analysis/item_previews(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_item_previews_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"item_previews"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /item_analysis/refresh_data(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_refresh_data_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"refresh_data"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /item_analysis/refresh_data(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_refresh_data_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"refresh_data"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /item_analysis/score_counts(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_score_counts_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"score_counts"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /item_analysis/score_counts(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_score_counts_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"score_counts"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /item_analysis/score_summary(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_score_summary_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"score_summary"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /item_analysis/score_summary(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_score_summary_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"score_summary"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /item_analysis/standards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_standards_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"standards"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /item_analysis/standards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_standards_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"standards"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /item_analysis/suggestions(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_suggestions_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"suggestions"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /item_analysis/suggestions(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const item_analysis_suggestions_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"suggestions"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:id/key(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const key_lesson_plan_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"key"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:id/key(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const key_lesson_plan_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"key"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /item_analysis/suggestions/klasses(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const klasses_item_analysis_suggestions_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"suggestions"],[2,[7,"/"],[2,[6,"klasses"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /item_analysis/suggestions/klasses(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const klasses_item_analysis_suggestions_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"suggestions"],[2,[7,"/"],[2,[6,"klasses"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lti_v13/content_items/launch(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const launch_lti_v13_content_items_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"content_items"],[2,[7,"/"],[2,[6,"launch"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lti_v13/content_items/launch(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const launch_lti_v13_content_items_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"content_items"],[2,[7,"/"],[2,[6,"launch"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /learnosity_answers_append_sessions(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const learnosity_answers_append_sessions_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"learnosity_answers_append_sessions"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /learnosity_answers_append_sessions(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const learnosity_answers_append_sessions_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"learnosity_answers_append_sessions"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /learnosity_answers_grid_view(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const learnosity_answers_grid_view_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"learnosity_answers_grid_view"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /learnosity_answers_grid_view(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const learnosity_answers_grid_view_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"learnosity_answers_grid_view"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /learnosity_answers_preview_configs(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const learnosity_answers_preview_configs_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"learnosity_answers_preview_configs"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /learnosity_answers_preview_configs(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const learnosity_answers_preview_configs_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"learnosity_answers_preview_configs"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /learnosity_answers_switch_session_context(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const learnosity_answers_switch_session_context_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"learnosity_answers_switch_session_context"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /learnosity_answers_switch_session_context(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const learnosity_answers_switch_session_context_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"learnosity_answers_switch_session_context"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/card_edits/:id(.:format)
 * @param {any} lesson_plan_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_card_edit_url = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"card_edits"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/card_edits/:id(.:format)
 * @param {any} lesson_plan_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_card_edit_path = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"card_edits"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/card_edits(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_card_edits_url = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"card_edits"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/card_edits(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_card_edits_path = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"card_edits"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/cards(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_cards_url = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"cards"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/cards(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_cards_path = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"cards"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/comments(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_comments_url = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"comments"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/comments(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_comments_path = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"comments"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plan_copies/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_copy_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plan_copies"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plan_copies/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_copy_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plan_copies"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/lesson_plan_copies(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_lesson_plan_copies_url = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"lesson_plan_copies"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/lesson_plan_copies(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_lesson_plan_copies_path = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"lesson_plan_copies"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/revision(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_revision_url = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"revision"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/revision(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plan_revision_path = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"revision"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plans_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plans_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /item_analysis/suggestions/lesson_plans(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plans_item_analysis_suggestions_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"suggestions"],[2,[7,"/"],[2,[6,"lesson_plans"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /item_analysis/suggestions/lesson_plans(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plans_item_analysis_suggestions_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"suggestions"],[2,[7,"/"],[2,[6,"lesson_plans"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /resources/:id/lesson_plans(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plans_resource_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"lesson_plans"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /resources/:id/lesson_plans(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lesson_plans_resource_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"lesson_plans"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/students/:id/link_to_google_classroom(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const link_to_google_classroom_teacher_student_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"students"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"link_to_google_classroom"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/students/:id/link_to_google_classroom(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const link_to_google_classroom_teacher_student_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"students"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"link_to_google_classroom"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /live_learn/:id/class/:klass_id(.:format)
 * @param {any} id
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const live_learn_klass_url = /*#__PURE__*/ __route({"id":{"r":true},"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"live_learn"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"class"],[2,[7,"/"],[2,[3,"klass_id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /live_learn/:id/class/:klass_id(.:format)
 * @param {any} id
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const live_learn_klass_path = /*#__PURE__*/ __route({"id":{"r":true},"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"live_learn"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"class"],[2,[7,"/"],[2,[3,"klass_id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /login(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const login_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"login"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /login(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const login_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"login"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /logout(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const logout_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"logout"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /logout(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const logout_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"logout"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /lti/configs/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lti_config_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lti"],[2,[7,"/"],[2,[6,"configs"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lti/configs/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lti_config_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lti"],[2,[7,"/"],[2,[6,"configs"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lti/security_signatures(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lti_security_signatures_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti"],[2,[7,"/"],[2,[6,"security_signatures"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /lti/security_signatures(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lti_security_signatures_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti"],[2,[7,"/"],[2,[6,"security_signatures"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /lti_v13/configs/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lti_v13_config_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"configs"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lti_v13/configs/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lti_v13_config_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"configs"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lti_v13/jwks(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lti_v13_jwks_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"jwks"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /lti_v13/jwks(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lti_v13_jwks_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"jwks"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /lti_v13/login_initiations(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lti_v13_login_initiations_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"login_initiations"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /lti_v13/login_initiations(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lti_v13_login_initiations_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"login_initiations"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /lti_v13/observer_session(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lti_v13_observer_session_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"observer_session"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /lti_v13/observer_session(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lti_v13_observer_session_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"observer_session"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /assignments/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /assignments/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/assignment_reports(.:format)
 * @param {any} lz_code_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_assignment_reports_url = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"assignment_reports"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/assignment_reports(.:format)
 * @param {any} lz_code_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_assignment_reports_path = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"assignment_reports"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/google_classroom_course_assignments(.:format)
 * @param {any} lz_code_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_google_classroom_course_assignments_url = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"google_classroom_course_assignments"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/google_classroom_course_assignments(.:format)
 * @param {any} lz_code_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_google_classroom_course_assignments_path = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"google_classroom_course_assignments"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/classes/:id(.:format)
 * @param {any} lz_code_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_klass_url = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/classes/:id(.:format)
 * @param {any} lz_code_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_klass_path = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/classes(.:format)
 * @param {any} lz_code_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_klasses_url = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"classes"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/classes(.:format)
 * @param {any} lz_code_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_klasses_path = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"classes"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/lz_code_klasses/:id(.:format)
 * @param {any} lz_code_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_lz_code_klass_url = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"lz_code_klasses"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/lz_code_klasses/:id(.:format)
 * @param {any} lz_code_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_lz_code_klass_path = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"lz_code_klasses"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/views/:id(.:format)
 * @param {any} lz_code_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_lz_code_view_url = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"views"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/views/:id(.:format)
 * @param {any} lz_code_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_lz_code_view_path = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"views"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/views(.:format)
 * @param {any} lz_code_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_lz_code_views_url = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"views"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/views(.:format)
 * @param {any} lz_code_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_lz_code_views_path = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"views"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/student_responses/:id(.:format)
 * @param {any} lz_code_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_student_response_url = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"student_responses"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/student_responses/:id(.:format)
 * @param {any} lz_code_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_student_response_path = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"student_responses"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/gallery/:id(.:format)
 * @param {any} lz_code_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_thinksphere_peer_feedback_url = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"gallery"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /assignments/:lz_code_id/gallery/:id(.:format)
 * @param {any} lz_code_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_code_thinksphere_peer_feedback_path = /*#__PURE__*/ __route({"lz_code_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[2,[7,"/"],[2,[3,"lz_code_id"],[2,[7,"/"],[2,[6,"gallery"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /assignments(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_codes_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /assignments(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lz_codes_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"assignments"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /lzui/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lzui_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lzui"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /lzui/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lzui_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lzui"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /lzui(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lzui_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lzui"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /lzui(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const lzui_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lzui"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /audios/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_audio_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"audios"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /audios/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_audio_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"audios"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /sites/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_customer_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"sites"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /sites/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_customer_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"sites"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /documents/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_document_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"documents"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /documents/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_document_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"documents"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /geogebra_items/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_geogebra_item_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"geogebra_items"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /geogebra_items/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_geogebra_item_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"geogebra_items"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /google_classroom/auth_completions/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_google_classroom_auth_completion_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"auth_completions"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /google_classroom/auth_completions/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_google_classroom_auth_completion_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"auth_completions"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /google_classroom/auth_initiations/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_google_classroom_auth_initiation_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"auth_initiations"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /google_classroom/auth_initiations/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_google_classroom_auth_initiation_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"auth_initiations"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /google_classroom/customer_auth_completions/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_google_classroom_customer_auth_completion_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"customer_auth_completions"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /google_classroom/customer_auth_completions/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_google_classroom_customer_auth_completion_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"google_classroom"],[2,[7,"/"],[2,[6,"customer_auth_completions"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /htmls/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_html_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"htmls"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /htmls/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_html_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"htmls"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /hyperlinks/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_hyperlink_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"hyperlinks"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /hyperlinks/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_hyperlink_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"hyperlinks"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /images/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_image_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"images"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /images/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_image_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"images"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_lesson_plan_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_lesson_plan_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /lti_v13/observer_session/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_lti_v13_observer_session_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"observer_session"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lti_v13/observer_session/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_lti_v13_observer_session_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"observer_session"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /password_resets/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_password_reset_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"password_resets"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /password_resets/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_password_reset_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"password_resets"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /resources/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_resource_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /resources/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_resource_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /session/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_session_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"session"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /session/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_session_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"session"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/standard_initiatives/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_site_admin_standard_initiative_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_initiatives"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/standard_initiatives/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_site_admin_standard_initiative_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_initiatives"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /slides/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_slide_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"slides"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /slides/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_slide_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"slides"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /spreadsheets/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_spreadsheet_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"spreadsheets"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /spreadsheets/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_spreadsheet_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"spreadsheets"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /student/session/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_student_session_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"session"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /student/session/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_student_session_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"session"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /user/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_user_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"user"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /user/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_user_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"user"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /videos/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_video_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"videos"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /videos/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_video_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"videos"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /wikis/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_wiki_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"wikis"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /wikis/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const new_wiki_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"wikis"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /org_switcher(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const org_switcher_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"org_switcher"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /org_switcher(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const org_switcher_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"org_switcher"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const page_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const page_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /password_resets/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const password_reset_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"password_resets"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /password_resets/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const password_reset_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"password_resets"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /password_resets(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const password_resets_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"password_resets"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /password_resets(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const password_resets_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"password_resets"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /lz_code_view/:lz_code_view_id/peer_to_peer_feedbacks/:peer_to_peer_feedback_id(.:format)
 * @param {any} lz_code_view_id
 * @param {any} peer_to_peer_feedback_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const peer_to_peer_feedback_url = /*#__PURE__*/ __route({"lz_code_view_id":{"r":true},"peer_to_peer_feedback_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lz_code_view"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"peer_to_peer_feedbacks"],[2,[7,"/"],[2,[3,"peer_to_peer_feedback_id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /lz_code_view/:lz_code_view_id/peer_to_peer_feedbacks/:peer_to_peer_feedback_id(.:format)
 * @param {any} lz_code_view_id
 * @param {any} peer_to_peer_feedback_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const peer_to_peer_feedback_path = /*#__PURE__*/ __route({"lz_code_view_id":{"r":true},"peer_to_peer_feedback_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lz_code_view"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"peer_to_peer_feedbacks"],[2,[7,"/"],[2,[3,"peer_to_peer_feedback_id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /lz_code_view/:lz_code_view_id/peer_to_peer_feedbacks(.:format)
 * @param {any} lz_code_view_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const peer_to_peer_feedbacks_url = /*#__PURE__*/ __route({"lz_code_view_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lz_code_view"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"peer_to_peer_feedbacks"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lz_code_view/:lz_code_view_id/peer_to_peer_feedbacks(.:format)
 * @param {any} lz_code_view_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const peer_to_peer_feedbacks_path = /*#__PURE__*/ __route({"lz_code_view_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lz_code_view"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"peer_to_peer_feedbacks"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lz_code_view/:lz_code_view_id/peer_to_peer_texts(.:format)
 * @param {any} lz_code_view_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const peer_to_peer_texts_url = /*#__PURE__*/ __route({"lz_code_view_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lz_code_view"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"peer_to_peer_texts"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lz_code_view/:lz_code_view_id/peer_to_peer_texts(.:format)
 * @param {any} lz_code_view_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const peer_to_peer_texts_path = /*#__PURE__*/ __route({"lz_code_view_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lz_code_view"],[2,[7,"/"],[2,[3,"lz_code_view_id"],[2,[7,"/"],[2,[6,"peer_to_peer_texts"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:id/people/rosterview(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const people_rosterview_teacher_klass_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"people"],[2,[7,"/"],[2,[6,"rosterview"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:id/people/rosterview(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const people_rosterview_teacher_klass_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"people"],[2,[7,"/"],[2,[6,"rosterview"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:id/people(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const people_teacher_klass_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"people"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:id/people(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const people_teacher_klass_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"people"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /personal_copies(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const personal_copies_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"personal_copies"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /personal_copies(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const personal_copies_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"personal_copies"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /pilot_guide/checklist(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const pilot_guide_checklist_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"pilot_guide"],[2,[7,"/"],[2,[6,"checklist"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /pilot_guide/checklist(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const pilot_guide_checklist_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"pilot_guide"],[2,[7,"/"],[2,[6,"checklist"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /pilot_guide/companion_guide(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const pilot_guide_companion_guide_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"pilot_guide"],[2,[7,"/"],[2,[6,"companion_guide"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /pilot_guide/companion_guide(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const pilot_guide_companion_guide_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"pilot_guide"],[2,[7,"/"],[2,[6,"companion_guide"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /practice_sets(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const practice_sets_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"practice_sets"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /practice_sets(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const practice_sets_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"practice_sets"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:id/presenter(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const presenter_lesson_plan_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"presenter"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:id/presenter(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const presenter_lesson_plan_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"presenter"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:id/preview(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const preview_lesson_plan_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"preview"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:id/preview(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const preview_lesson_plan_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"preview"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /printable/handouts/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const printable_handout_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"printable"],[2,[7,"/"],[2,[6,"handouts"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /printable/handouts/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const printable_handout_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"printable"],[2,[7,"/"],[2,[6,"handouts"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /printable/resources/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const printable_resource_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"printable"],[2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /printable/resources/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const printable_resource_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"printable"],[2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /printable/side_by_sides/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const printable_side_by_side_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"printable"],[2,[7,"/"],[2,[6,"side_by_sides"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /printable/side_by_sides/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const printable_side_by_side_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"printable"],[2,[7,"/"],[2,[6,"side_by_sides"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /printable/teacher_references/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const printable_teacher_reference_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"printable"],[2,[7,"/"],[2,[6,"teacher_references"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /printable/teacher_references/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const printable_teacher_reference_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"printable"],[2,[7,"/"],[2,[6,"teacher_references"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /printable/with_notes/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const printable_with_note_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"printable"],[2,[7,"/"],[2,[6,"with_notes"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /printable/with_notes/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const printable_with_note_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"printable"],[2,[7,"/"],[2,[6,"with_notes"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /privacy(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const privacy_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"privacy"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /privacy(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const privacy_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"privacy"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /rails/active_storage/representations/redirect/:signed_blob_id/:variation_key/*filename(.:format)
 * @param {any} signed_blob_id
 * @param {any} variation_key
 * @param {any} filename
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const rails_blob_representation_url = /*#__PURE__*/ __route({"signed_blob_id":{"r":true},"variation_key":{"r":true},"filename":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"representations"],[2,[7,"/"],[2,[6,"redirect"],[2,[7,"/"],[2,[3,"signed_blob_id"],[2,[7,"/"],[2,[3,"variation_key"],[2,[7,"/"],[2,[5,[3,"filename"]],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /rails/active_storage/representations/redirect/:signed_blob_id/:variation_key/*filename(.:format)
 * @param {any} signed_blob_id
 * @param {any} variation_key
 * @param {any} filename
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const rails_blob_representation_path = /*#__PURE__*/ __route({"signed_blob_id":{"r":true},"variation_key":{"r":true},"filename":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"representations"],[2,[7,"/"],[2,[6,"redirect"],[2,[7,"/"],[2,[3,"signed_blob_id"],[2,[7,"/"],[2,[3,"variation_key"],[2,[7,"/"],[2,[5,[3,"filename"]],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /rails/active_storage/representations/proxy/:signed_blob_id/:variation_key/*filename(.:format)
 * @param {any} signed_blob_id
 * @param {any} variation_key
 * @param {any} filename
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const rails_blob_representation_proxy_url = /*#__PURE__*/ __route({"signed_blob_id":{"r":true},"variation_key":{"r":true},"filename":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"representations"],[2,[7,"/"],[2,[6,"proxy"],[2,[7,"/"],[2,[3,"signed_blob_id"],[2,[7,"/"],[2,[3,"variation_key"],[2,[7,"/"],[2,[5,[3,"filename"]],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /rails/active_storage/representations/proxy/:signed_blob_id/:variation_key/*filename(.:format)
 * @param {any} signed_blob_id
 * @param {any} variation_key
 * @param {any} filename
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const rails_blob_representation_proxy_path = /*#__PURE__*/ __route({"signed_blob_id":{"r":true},"variation_key":{"r":true},"filename":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"representations"],[2,[7,"/"],[2,[6,"proxy"],[2,[7,"/"],[2,[3,"signed_blob_id"],[2,[7,"/"],[2,[3,"variation_key"],[2,[7,"/"],[2,[5,[3,"filename"]],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /rails/active_storage/direct_uploads(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const rails_direct_uploads_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"direct_uploads"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /rails/active_storage/direct_uploads(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const rails_direct_uploads_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"direct_uploads"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /rails/active_storage/disk/:encoded_key/*filename(.:format)
 * @param {any} encoded_key
 * @param {any} filename
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const rails_disk_service_url = /*#__PURE__*/ __route({"encoded_key":{"r":true},"filename":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"disk"],[2,[7,"/"],[2,[3,"encoded_key"],[2,[7,"/"],[2,[5,[3,"filename"]],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /rails/active_storage/disk/:encoded_key/*filename(.:format)
 * @param {any} encoded_key
 * @param {any} filename
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const rails_disk_service_path = /*#__PURE__*/ __route({"encoded_key":{"r":true},"filename":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"disk"],[2,[7,"/"],[2,[3,"encoded_key"],[2,[7,"/"],[2,[5,[3,"filename"]],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /rails/active_storage/blobs/redirect/:signed_id/*filename(.:format)
 * @param {any} signed_id
 * @param {any} filename
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const rails_service_blob_url = /*#__PURE__*/ __route({"signed_id":{"r":true},"filename":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"blobs"],[2,[7,"/"],[2,[6,"redirect"],[2,[7,"/"],[2,[3,"signed_id"],[2,[7,"/"],[2,[5,[3,"filename"]],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /rails/active_storage/blobs/redirect/:signed_id/*filename(.:format)
 * @param {any} signed_id
 * @param {any} filename
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const rails_service_blob_path = /*#__PURE__*/ __route({"signed_id":{"r":true},"filename":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"blobs"],[2,[7,"/"],[2,[6,"redirect"],[2,[7,"/"],[2,[3,"signed_id"],[2,[7,"/"],[2,[5,[3,"filename"]],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /rails/active_storage/blobs/proxy/:signed_id/*filename(.:format)
 * @param {any} signed_id
 * @param {any} filename
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const rails_service_blob_proxy_url = /*#__PURE__*/ __route({"signed_id":{"r":true},"filename":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"blobs"],[2,[7,"/"],[2,[6,"proxy"],[2,[7,"/"],[2,[3,"signed_id"],[2,[7,"/"],[2,[5,[3,"filename"]],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /rails/active_storage/blobs/proxy/:signed_id/*filename(.:format)
 * @param {any} signed_id
 * @param {any} filename
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const rails_service_blob_proxy_path = /*#__PURE__*/ __route({"signed_id":{"r":true},"filename":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"blobs"],[2,[7,"/"],[2,[6,"proxy"],[2,[7,"/"],[2,[3,"signed_id"],[2,[7,"/"],[2,[5,[3,"filename"]],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /audios/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_audio_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"audios"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /audios/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_audio_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"audios"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /documents/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_document_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"documents"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /documents/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_document_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"documents"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /geogebra_items/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_geogebra_item_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"geogebra_items"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /geogebra_items/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_geogebra_item_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"geogebra_items"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /htmls/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_html_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"htmls"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /htmls/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_html_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"htmls"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /hyperlinks/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_hyperlink_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"hyperlinks"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /hyperlinks/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_hyperlink_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"hyperlinks"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /images/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_image_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"images"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /images/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_image_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"images"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /slides/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_slide_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"slides"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /slides/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_slide_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"slides"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /spreadsheets/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_spreadsheet_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"spreadsheets"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /spreadsheets/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_spreadsheet_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"spreadsheets"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /videos/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_video_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"videos"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /videos/:id/related-lessons(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const related_lessons_video_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"videos"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"related-lessons"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/card_edits/reorder(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const reorder_lesson_plan_card_edits_url = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"card_edits"],[2,[7,"/"],[2,[6,"reorder"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/card_edits/reorder(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const reorder_lesson_plan_card_edits_path = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"card_edits"],[2,[7,"/"],[2,[6,"reorder"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/cards/reorder(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const reorder_lesson_plan_cards_url = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"cards"],[2,[7,"/"],[2,[6,"reorder"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /lesson_plans/:lesson_plan_id/cards/reorder(.:format)
 * @param {any} lesson_plan_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const reorder_lesson_plan_cards_path = /*#__PURE__*/ __route({"lesson_plan_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"lesson_plans"],[2,[7,"/"],[2,[3,"lesson_plan_id"],[2,[7,"/"],[2,[6,"cards"],[2,[7,"/"],[2,[6,"reorder"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /resources/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /resources/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /audios/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_audio_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"audios"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /audios/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_audio_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"audios"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /documents/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_document_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"documents"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /documents/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_document_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"documents"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /geogebra_items/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_geogebra_item_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"geogebra_items"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /geogebra_items/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_geogebra_item_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"geogebra_items"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /htmls/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_html_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"htmls"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /htmls/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_html_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"htmls"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /hyperlinks/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_hyperlink_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"hyperlinks"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /hyperlinks/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_hyperlink_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"hyperlinks"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /images/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_image_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"images"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /images/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_image_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"images"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /slides/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_slide_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"slides"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /slides/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_slide_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"slides"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /spreadsheets/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_spreadsheet_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"spreadsheets"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /spreadsheets/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_spreadsheet_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"spreadsheets"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /videos/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_video_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"videos"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /videos/:id/resource-content(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_content_video_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"videos"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"resource-content"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /resources/:resource_id/revision(.:format)
 * @param {any} resource_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_revision_url = /*#__PURE__*/ __route({"resource_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"resource_id"],[2,[7,"/"],[2,[6,"revision"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /resources/:resource_id/revision(.:format)
 * @param {any} resource_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resource_revision_path = /*#__PURE__*/ __route({"resource_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"resource_id"],[2,[7,"/"],[2,[6,"revision"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /resources(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resources_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"resources"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /resources(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const resources_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"resources"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /site_admin/settings/restore(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const restore_site_admin_settings_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"settings"],[2,[7,"/"],[2,[6,"restore"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/settings/restore(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const restore_site_admin_settings_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"settings"],[2,[7,"/"],[2,[6,"restore"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /revision_publish_jobs/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const revision_publish_job_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"revision_publish_jobs"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /revision_publish_jobs/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const revision_publish_job_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"revision_publish_jobs"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /revision_publish_jobs(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const revision_publish_jobs_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"revision_publish_jobs"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /revision_publish_jobs(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const revision_publish_jobs_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"revision_publish_jobs"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /robots.:format
 * @param {any} format
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const robots_url = /*#__PURE__*/ __route({"format":{"r":true}}, [2,[7,"/"],[2,[6,"robots"],[2,[8,"."],[3,"format"]]]], true);

/**
 * Generates rails route to
 * /robots.:format
 * @param {any} format
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const robots_path = /*#__PURE__*/ __route({"format":{"r":true}}, [2,[7,"/"],[2,[6,"robots"],[2,[8,"."],[3,"format"]]]]);

/**
 * Generates rails route to
 * /
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const root_url = /*#__PURE__*/ __route({}, [7,"/"], true);

/**
 * Generates rails route to
 * /
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const root_path = /*#__PURE__*/ __route({}, [7,"/"]);

/**
 * Generates rails route to
 * /lti/content_items(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const root_lti_content_items_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti"],[2,[7,"/"],[2,[6,"content_items"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /lti/content_items(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const root_lti_content_items_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti"],[2,[7,"/"],[2,[6,"content_items"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /lti_v13/content_items(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const root_lti_v13_content_items_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"content_items"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /lti_v13/content_items(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const root_lti_v13_content_items_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"lti_v13"],[2,[7,"/"],[2,[6,"content_items"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /comments/:id/scanii(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const scanii_comment_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"comments"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"scanii"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /comments/:id/scanii(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const scanii_comment_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"comments"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"scanii"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /resources/:id/scanii(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const scanii_resource_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"scanii"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /resources/:id/scanii(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const scanii_resource_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"resources"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"scanii"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /school_districts(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const school_districts_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"school_districts"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /school_districts(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const school_districts_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"school_districts"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /item_analysis/suggestions/schools(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const schools_item_analysis_suggestions_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"suggestions"],[2,[7,"/"],[2,[6,"schools"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /item_analysis/suggestions/schools(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const schools_item_analysis_suggestions_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"suggestions"],[2,[7,"/"],[2,[6,"schools"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /search(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const search_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"search"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /search(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const search_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"search"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /session(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const session_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"session"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /session(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const session_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"session"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /l/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const short_lesson_plan_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"l"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /l/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const short_lesson_plan_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"l"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /r/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const short_resource_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"r"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /r/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const short_resource_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"r"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /sidekiq
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const sidekiq_web_url = /*#__PURE__*/ __route({}, [2,[7,"/"],[6,"sidekiq"]], true);

/**
 * Generates rails route to
 * /sidekiq
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const sidekiq_web_path = /*#__PURE__*/ __route({}, [2,[7,"/"],[6,"sidekiq"]]);

/**
 * Generates rails route to
 * /site_admin/admin_accesses/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_admin_access_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"admin_accesses"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/admin_accesses/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_admin_access_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"admin_accesses"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/admin_accesses(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_admin_accesses_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"admin_accesses"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/admin_accesses(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_admin_accesses_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"admin_accesses"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/admin_ai_access/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_admin_ai_access_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"admin_ai_access"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/admin_ai_access/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_admin_ai_access_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"admin_ai_access"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/admin_ai_access(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_admin_ai_access_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"admin_ai_access"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/admin_ai_access(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_admin_ai_access_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"admin_ai_access"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/ai_access(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_ai_access_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"ai_access"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/ai_access(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_ai_access_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"ai_access"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/archived_users_deletions(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_archived_users_deletions_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"archived_users_deletions"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/archived_users_deletions(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_archived_users_deletions_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"archived_users_deletions"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/clever(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_clever_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"clever"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/clever(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_clever_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"clever"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/collections/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collection_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/collections/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collection_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/collections/:collection_id/export(.:format)
 * @param {any} collection_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collection_collection_export_url = /*#__PURE__*/ __route({"collection_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"collection_id"],[2,[7,"/"],[2,[6,"export"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/collections/:collection_id/export(.:format)
 * @param {any} collection_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collection_collection_export_path = /*#__PURE__*/ __route({"collection_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"collection_id"],[2,[7,"/"],[2,[6,"export"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/collections/:collection_id/publish(.:format)
 * @param {any} collection_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collection_collection_publish_job_url = /*#__PURE__*/ __route({"collection_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"collection_id"],[2,[7,"/"],[2,[6,"publish"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/collections/:collection_id/publish(.:format)
 * @param {any} collection_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collection_collection_publish_job_path = /*#__PURE__*/ __route({"collection_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"collection_id"],[2,[7,"/"],[2,[6,"publish"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/collections/:collection_id/collection_shares/:id(.:format)
 * @param {any} collection_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collection_collection_share_url = /*#__PURE__*/ __route({"collection_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"collection_id"],[2,[7,"/"],[2,[6,"collection_shares"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/collections/:collection_id/collection_shares/:id(.:format)
 * @param {any} collection_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collection_collection_share_path = /*#__PURE__*/ __route({"collection_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"collection_id"],[2,[7,"/"],[2,[6,"collection_shares"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/collections/:collection_id/collection_shares(.:format)
 * @param {any} collection_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collection_collection_shares_url = /*#__PURE__*/ __route({"collection_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"collection_id"],[2,[7,"/"],[2,[6,"collection_shares"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/collections/:collection_id/collection_shares(.:format)
 * @param {any} collection_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collection_collection_shares_path = /*#__PURE__*/ __route({"collection_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"collection_id"],[2,[7,"/"],[2,[6,"collection_shares"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/collections/:collection_id/shares_batch(.:format)
 * @param {any} collection_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collection_shares_batch_url = /*#__PURE__*/ __route({"collection_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"collection_id"],[2,[7,"/"],[2,[6,"shares_batch"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/collections/:collection_id/shares_batch(.:format)
 * @param {any} collection_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collection_shares_batch_path = /*#__PURE__*/ __route({"collection_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[2,[7,"/"],[2,[3,"collection_id"],[2,[7,"/"],[2,[6,"shares_batch"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/collections(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collections_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/collections(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_collections_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"collections"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/curriculum_sitemap/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_curriculum_sitemap_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"curriculum_sitemap"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/curriculum_sitemap/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_curriculum_sitemap_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"curriculum_sitemap"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/curriculum_sitemap(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_curriculum_sitemap_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"curriculum_sitemap"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/curriculum_sitemap(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_curriculum_sitemap_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"curriculum_sitemap"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/feature_flag_debug(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_feature_flag_debug_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"feature_flag_debug"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/feature_flag_debug(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_feature_flag_debug_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"feature_flag_debug"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/lms_integrations/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_lms_integration_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"lms_integrations"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/lms_integrations/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_lms_integration_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"lms_integrations"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/lms_integrations(/page/:page)(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_lms_integrations_url = /*#__PURE__*/ __route({"page":{"d":1},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"lms_integrations"],[2,[1,[2,[7,"/"],[2,[6,"page"],[2,[7,"/"],[3,"page"]]]]],[1,[2,[8,"."],[3,"format"]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/lms_integrations(/page/:page)(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_lms_integrations_path = /*#__PURE__*/ __route({"page":{"d":1},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"lms_integrations"],[2,[1,[2,[7,"/"],[2,[6,"page"],[2,[7,"/"],[3,"page"]]]]],[1,[2,[8,"."],[3,"format"]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/lti_v13_integrations/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_lti_v13_integration_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"lti_v13_integrations"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/lti_v13_integrations/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_lti_v13_integration_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"lti_v13_integrations"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/lti_v13_integrations(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_lti_v13_integrations_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"lti_v13_integrations"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/lti_v13_integrations(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_lti_v13_integrations_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"lti_v13_integrations"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/question_banks(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_question_banks_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"question_banks"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/question_banks(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_question_banks_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"question_banks"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/recurring_exports/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_recurring_export_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"recurring_exports"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/recurring_exports/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_recurring_export_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"recurring_exports"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/recurring_exports(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_recurring_exports_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"recurring_exports"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/recurring_exports(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_recurring_exports_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"recurring_exports"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/recurring_import(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_recurring_import_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"recurring_import"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/recurring_import(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_recurring_import_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"recurring_import"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_root_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /site_admin(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_root_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /site_admin/schools/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_school_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"schools"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/schools/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_school_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"schools"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/schools/:school_id/merge_schools/:id(.:format)
 * @param {any} school_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_school_merge_school_url = /*#__PURE__*/ __route({"school_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"schools"],[2,[7,"/"],[2,[3,"school_id"],[2,[7,"/"],[2,[6,"merge_schools"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/schools/:school_id/merge_schools/:id(.:format)
 * @param {any} school_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_school_merge_school_path = /*#__PURE__*/ __route({"school_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"schools"],[2,[7,"/"],[2,[3,"school_id"],[2,[7,"/"],[2,[6,"merge_schools"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/schools(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_schools_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"schools"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/schools(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_schools_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"schools"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/settings(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_settings_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"settings"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/settings(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_settings_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"settings"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/single_integration(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_single_integration_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"single_integration"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/single_integration(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_single_integration_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"single_integration"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/sis_integration(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_sis_integration_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"sis_integration"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/sis_integration(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_sis_integration_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"sis_integration"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/standards/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standards"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/standards/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standards"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/standard_downloads/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_download_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_downloads"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/standard_downloads/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_download_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_downloads"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/standard_downloads(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_downloads_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_downloads"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/standard_downloads(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_downloads_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_downloads"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/standard_imports/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_import_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_imports"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/standard_imports/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_import_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_imports"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/standard_imports(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_imports_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_imports"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/standard_imports(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_imports_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_imports"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/standard_initiatives/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_initiative_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_initiatives"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/standard_initiatives/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_initiative_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_initiatives"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/standard_initiatives(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_initiatives_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_initiatives"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/standard_initiatives(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standard_initiatives_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standard_initiatives"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/standards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standards_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standards"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/standards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_standards_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"standards"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/users/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_user_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"users"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/users/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_user_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"users"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/user_import_csv_previews(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_user_import_csv_previews_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"user_import_csv_previews"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/user_import_csv_previews(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_user_import_csv_previews_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"user_import_csv_previews"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/user_imports/:user_import_id/original(.:format)
 * @param {any} user_import_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_user_import_original_url = /*#__PURE__*/ __route({"user_import_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"user_imports"],[2,[7,"/"],[2,[3,"user_import_id"],[2,[7,"/"],[2,[6,"original"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/user_imports/:user_import_id/original(.:format)
 * @param {any} user_import_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_user_import_original_path = /*#__PURE__*/ __route({"user_import_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"user_imports"],[2,[7,"/"],[2,[3,"user_import_id"],[2,[7,"/"],[2,[6,"original"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/user_imports/:user_import_id/report(.:format)
 * @param {any} user_import_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_user_import_report_url = /*#__PURE__*/ __route({"user_import_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"user_imports"],[2,[7,"/"],[2,[3,"user_import_id"],[2,[7,"/"],[2,[6,"report"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/user_imports/:user_import_id/report(.:format)
 * @param {any} user_import_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_user_import_report_path = /*#__PURE__*/ __route({"user_import_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"user_imports"],[2,[7,"/"],[2,[3,"user_import_id"],[2,[7,"/"],[2,[6,"report"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/user_imports(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_user_imports_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"user_imports"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/user_imports(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_user_imports_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"user_imports"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/user_reports(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_user_reports_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"user_reports"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/user_reports(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_user_reports_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"user_reports"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /site_admin/users(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_users_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"users"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /site_admin/users(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const site_admin_users_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"site_admin"],[2,[7,"/"],[2,[6,"users"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /slides/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const slide_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"slides"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /slides/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const slide_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"slides"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /slides(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const slides_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"slides"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /slides(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const slides_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"slides"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /spreadsheets/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const spreadsheet_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"spreadsheets"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /spreadsheets/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const spreadsheet_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"spreadsheets"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /spreadsheets(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const spreadsheets_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"spreadsheets"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /spreadsheets(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const spreadsheets_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"spreadsheets"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /standards/:code(.:format)
 * @param {any} code
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const standard_url = /*#__PURE__*/ __route({"code":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"standards"],[2,[7,"/"],[2,[3,"code"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /standards/:code(.:format)
 * @param {any} code
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const standard_path = /*#__PURE__*/ __route({"code":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"standards"],[2,[7,"/"],[2,[3,"code"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /standards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const standards_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"standards"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /standards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const standards_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"standards"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /standards_coverage_tables/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const standards_coverage_table_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"standards_coverage_tables"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /standards_coverage_tables/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const standards_coverage_table_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"standards_coverage_tables"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /standards_coverage_tables(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const standards_coverage_tables_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"standards_coverage_tables"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /standards_coverage_tables(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const standards_coverage_tables_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"standards_coverage_tables"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /student_data_reports(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_data_reports_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student_data_reports"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /student_data_reports(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_data_reports_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student_data_reports"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /student/enhanced_login(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_enhanced_login_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"enhanced_login"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /student/enhanced_login(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_enhanced_login_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"enhanced_login"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /student/classes/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_klass_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /student/classes/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_klass_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /student/classes(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_klasses_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"classes"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /student/classes(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_klasses_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"classes"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /student/login(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_login_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"login"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /student/login(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_login_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"login"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /student/logout(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_logout_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"logout"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /student/logout(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_logout_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"logout"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /student/rewards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_rewards_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"rewards"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /student/rewards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_rewards_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"rewards"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /student/rewards/claim_sticker(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_rewards_claim_sticker_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"rewards"],[2,[7,"/"],[2,[6,"claim_sticker"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /student/rewards/claim_sticker(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_rewards_claim_sticker_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"rewards"],[2,[7,"/"],[2,[6,"claim_sticker"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /student/rewards/progress(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_rewards_progress_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"rewards"],[2,[7,"/"],[2,[6,"progress"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /student/rewards/progress(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_rewards_progress_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"rewards"],[2,[7,"/"],[2,[6,"progress"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /student/rewards/sticker_categories(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_rewards_sticker_categories_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"rewards"],[2,[7,"/"],[2,[6,"sticker_categories"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /student/rewards/sticker_categories(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_rewards_sticker_categories_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"rewards"],[2,[7,"/"],[2,[6,"sticker_categories"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /student/rewards/stuff(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_rewards_stuff_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"rewards"],[2,[7,"/"],[2,[6,"stuff"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /student/rewards/stuff(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_rewards_stuff_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"rewards"],[2,[7,"/"],[2,[6,"stuff"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /student(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_root_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /student(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_root_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /student/session(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_session_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"session"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /student/session(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_session_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"session"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /student/session/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_session_new_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"session"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /student/session/new(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_session_new_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"session"],[2,[7,"/"],[2,[6,"new"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /student/think_tile_claims(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_think_tile_claims_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"think_tile_claims"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /student/think_tile_claims(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_think_tile_claims_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"think_tile_claims"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /student/collections(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_tiles_collections_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"collections"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /student/collections(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_tiles_collections_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"collections"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /student_tools(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_tools_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student_tools"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /student_tools(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_tools_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student_tools"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /student/claim_rewards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_user_reward_claimed_assets_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"claim_rewards"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /student/claim_rewards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const student_user_reward_claimed_assets_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"student"],[2,[7,"/"],[2,[6,"claim_rewards"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:id/students(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const students_teacher_klass_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"students"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:id/students(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const students_teacher_klass_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[2,[7,"/"],[2,[6,"students"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /surveys/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const survey_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"surveys"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /surveys/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const survey_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"surveys"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /surveys/:survey_id/responses(.:format)
 * @param {any} survey_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const survey_responses_url = /*#__PURE__*/ __route({"survey_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"surveys"],[2,[7,"/"],[2,[3,"survey_id"],[2,[7,"/"],[2,[6,"responses"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /surveys/:survey_id/responses(.:format)
 * @param {any} survey_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const survey_responses_path = /*#__PURE__*/ __route({"survey_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"surveys"],[2,[7,"/"],[2,[3,"survey_id"],[2,[7,"/"],[2,[6,"responses"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /sync_barred_words(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const sync_barred_words_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"sync_barred_words"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /sync_barred_words(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const sync_barred_words_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"sync_barred_words"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /tags(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const tags_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"tags"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /tags(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const tags_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"tags"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /teacher/google_classroom_token(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_google_classroom_token_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"google_classroom_token"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/google_classroom_token(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_google_classroom_token_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"google_classroom_token"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/class_challenge(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_class_challenge_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"class_challenge"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/class_challenge(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_class_challenge_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"class_challenge"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/enrollments/:id(.:format)
 * @param {any} klass_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_enrollment_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"enrollments"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/enrollments/:id(.:format)
 * @param {any} klass_id
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_enrollment_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"enrollments"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/enrollments(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_enrollments_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"enrollments"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/enrollments(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_enrollments_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"enrollments"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/google_classroom_course(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_google_classroom_course_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"google_classroom_course"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/google_classroom_course(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_google_classroom_course_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"google_classroom_course"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/rosters(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_rosters_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"rosters"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/rosters(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_rosters_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"rosters"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/teacher_klasses(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_teacher_klasses_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"teacher_klasses"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/teacher_klasses(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_teacher_klasses_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"teacher_klasses"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/teachers(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_teachers_url = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"teachers"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes/:klass_id/teachers(.:format)
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klass_teachers_path = /*#__PURE__*/ __route({"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"teachers"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/classes(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klasses_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/classes(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_klasses_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"classes"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /teacher/login(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_login_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"login"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/login(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_login_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"login"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /teacher/rewards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_rewards_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"rewards"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/rewards(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_rewards_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"rewards"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /teacher/rewards/progress(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_rewards_progress_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"rewards"],[2,[7,"/"],[2,[6,"progress"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/rewards/progress(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_rewards_progress_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"rewards"],[2,[7,"/"],[2,[6,"progress"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/rewards/stuff(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_rewards_stuff_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"rewards"],[2,[7,"/"],[2,[6,"stuff"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/rewards/stuff(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_rewards_stuff_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"rewards"],[2,[7,"/"],[2,[6,"stuff"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_root_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /teacher(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_root_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /teacher/students(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_students_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"students"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/students(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_students_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"students"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /teacher/students_search(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_students_search_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"students_search"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/students_search(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_students_search_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"students_search"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /teacher/teachers_search(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_teachers_search_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"teachers_search"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/teachers_search(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_teachers_search_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"teachers_search"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /teacher/contents/:thinksphere_item_id/classes/:klass_id/assignments(.:format)
 * @param {any} thinksphere_item_id
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_thinksphere_item_klass_lz_codes_url = /*#__PURE__*/ __route({"thinksphere_item_id":{"r":true},"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"contents"],[2,[7,"/"],[2,[3,"thinksphere_item_id"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"assignments"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/contents/:thinksphere_item_id/classes/:klass_id/assignments(.:format)
 * @param {any} thinksphere_item_id
 * @param {any} klass_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_thinksphere_item_klass_lz_codes_path = /*#__PURE__*/ __route({"thinksphere_item_id":{"r":true},"klass_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"contents"],[2,[7,"/"],[2,[3,"thinksphere_item_id"],[2,[7,"/"],[2,[6,"classes"],[2,[7,"/"],[2,[3,"klass_id"],[2,[7,"/"],[2,[6,"assignments"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]]]]]);

/**
 * Generates rails route to
 * /teacher/contents/:thinksphere_item_id/classes(.:format)
 * @param {any} thinksphere_item_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_thinksphere_item_klasses_url = /*#__PURE__*/ __route({"thinksphere_item_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"contents"],[2,[7,"/"],[2,[3,"thinksphere_item_id"],[2,[7,"/"],[2,[6,"classes"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /teacher/contents/:thinksphere_item_id/classes(.:format)
 * @param {any} thinksphere_item_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teacher_thinksphere_item_klasses_path = /*#__PURE__*/ __route({"thinksphere_item_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"teacher"],[2,[7,"/"],[2,[6,"contents"],[2,[7,"/"],[2,[3,"thinksphere_item_id"],[2,[7,"/"],[2,[6,"classes"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /item_analysis/suggestions/teachers(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teachers_item_analysis_suggestions_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"suggestions"],[2,[7,"/"],[2,[6,"teachers"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /item_analysis/suggestions/teachers(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const teachers_item_analysis_suggestions_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"item_analysis"],[2,[7,"/"],[2,[6,"suggestions"],[2,[7,"/"],[2,[6,"teachers"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /tokens/eventing_token(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const tokens_eventing_token_index_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"tokens"],[2,[7,"/"],[2,[6,"eventing_token"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /tokens/eventing_token(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const tokens_eventing_token_index_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"tokens"],[2,[7,"/"],[2,[6,"eventing_token"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /tokens/jwks(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const tokens_jwks_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"tokens"],[2,[7,"/"],[2,[6,"jwks"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /tokens/jwks(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const tokens_jwks_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"tokens"],[2,[7,"/"],[2,[6,"jwks"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /rails/active_storage/disk/:encoded_token(.:format)
 * @param {any} encoded_token
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const update_rails_disk_service_url = /*#__PURE__*/ __route({"encoded_token":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"disk"],[2,[7,"/"],[2,[3,"encoded_token"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]], true);

/**
 * Generates rails route to
 * /rails/active_storage/disk/:encoded_token(.:format)
 * @param {any} encoded_token
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const update_rails_disk_service_path = /*#__PURE__*/ __route({"encoded_token":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"rails"],[2,[7,"/"],[2,[6,"active_storage"],[2,[7,"/"],[2,[6,"disk"],[2,[7,"/"],[2,[3,"encoded_token"],[1,[2,[8,"."],[3,"format"]]]]]]]]]]]);

/**
 * Generates rails route to
 * /user(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const user_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"user"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /user(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const user_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"user"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /user/deletion(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const user_deletion_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"user"],[2,[7,"/"],[2,[6,"deletion"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /user/deletion(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const user_deletion_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"user"],[2,[7,"/"],[2,[6,"deletion"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /users/:user_id/impersonation(.:format)
 * @param {any} user_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const user_impersonation_url = /*#__PURE__*/ __route({"user_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"users"],[2,[7,"/"],[2,[3,"user_id"],[2,[7,"/"],[2,[6,"impersonation"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /users/:user_id/impersonation(.:format)
 * @param {any} user_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const user_impersonation_path = /*#__PURE__*/ __route({"user_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"users"],[2,[7,"/"],[2,[3,"user_id"],[2,[7,"/"],[2,[6,"impersonation"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /videos/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const video_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"videos"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /videos/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const video_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"videos"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /videos(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const videos_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"videos"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /videos(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const videos_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"videos"],[1,[2,[8,"."],[3,"format"]]]]]);

/**
 * Generates rails route to
 * /wikis/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const wiki_url = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"wikis"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]], true);

/**
 * Generates rails route to
 * /wikis/:id(.:format)
 * @param {any} id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const wiki_path = /*#__PURE__*/ __route({"id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"wikis"],[2,[7,"/"],[2,[3,"id"],[1,[2,[8,"."],[3,"format"]]]]]]]);

/**
 * Generates rails route to
 * /wikis/:wiki_id/wiki_edits(.:format)
 * @param {any} wiki_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const wiki_wiki_edits_url = /*#__PURE__*/ __route({"wiki_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"wikis"],[2,[7,"/"],[2,[3,"wiki_id"],[2,[7,"/"],[2,[6,"wiki_edits"],[1,[2,[8,"."],[3,"format"]]]]]]]]], true);

/**
 * Generates rails route to
 * /wikis/:wiki_id/wiki_edits(.:format)
 * @param {any} wiki_id
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const wiki_wiki_edits_path = /*#__PURE__*/ __route({"wiki_id":{"r":true},"format":{}}, [2,[7,"/"],[2,[6,"wikis"],[2,[7,"/"],[2,[3,"wiki_id"],[2,[7,"/"],[2,[6,"wiki_edits"],[1,[2,[8,"."],[3,"format"]]]]]]]]]);

/**
 * Generates rails route to
 * /wikis(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const wikis_url = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"wikis"],[1,[2,[8,"."],[3,"format"]]]]], true);

/**
 * Generates rails route to
 * /wikis(.:format)
 * @param {object | undefined} options
 * @returns {string} route path
 */
export const wikis_path = /*#__PURE__*/ __route({"format":{}}, [2,[7,"/"],[2,[6,"wikis"],[1,[2,[8,"."],[3,"format"]]]]]);

